import enUS from "date-fns/locale/en-US";
import he from "date-fns/locale/he";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { valueForOnlyNumericInputTextFields } from "utils/helpers";

import { customersActions } from "../../../redux/slice";
import { CustomerPriceQuoteStatus } from "../../../types";

export const PRICE_QUOTE_LABELS = "CUSTOMERS.DETAILS.PRICE_QUOTES";

type PriceQuoteForm = {
  projectType: string;
  revisionNumber: string;
  totalValue: string;
  status: CustomerPriceQuoteStatus;
  validUntil: string;
};

const emptyForm = (): PriceQuoteForm => ({
  projectType: "",
  revisionNumber: "",
  totalValue: "",
  status: CustomerPriceQuoteStatus.DRAFT,
  validUntil: "",
});

const toDateString = (date: Date) => {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

export const useAddPriceQuoteModal = (onClose: () => void) => {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const [form, setForm] = useState<PriceQuoteForm>(emptyForm);
  const [isDateOpen, setIsDateOpen] = useState(false);

  const close = () => {
    setForm(emptyForm());
    setIsDateOpen(false);
    onClose();
  };

  const setProjectType = (projectType: string) => {
    setForm((current) => ({ ...current, projectType }));
  };

  const setNumericField = (
    key: "revisionNumber" | "totalValue",
    value: string
  ) => {
    setForm((current) => ({
      ...current,
      [key]: valueForOnlyNumericInputTextFields(value),
    }));
  };

  const setStatus = (status: CustomerPriceQuoteStatus) => {
    setForm((current) => ({ ...current, status }));
  };

  const setValidUntil = (date: Date | null) => {
    setForm((current) => ({
      ...current,
      validUntil: date && !Number.isNaN(date.getTime()) ? toDateString(date) : "",
    }));
  };

  const isValid = Boolean(
    form.projectType &&
      form.revisionNumber &&
      form.totalValue &&
      form.validUntil
  );

  const submit = () => {
    if (!isValid) return;

    dispatch(
      customersActions.addCustomerPriceQuote({
        id: String(Date.now()),
        quoteNumber: "",
        createdAt: toDateString(new Date()),
        projectTypeKey: form.projectType,
        revisionNumber: Number(form.revisionNumber),
        totalValue: Number(form.totalValue),
        status: form.status,
        validUntil: form.validUntil,
      })
    );
    close();
  };

  return {
    form,
    isValid,
    isDateOpen,
    setIsDateOpen,
    dateLocale: i18n.language === "hw" || i18n.language === "he" ? he : enUS,
    close,
    setProjectType,
    setNumericField,
    setStatus,
    setValidUntil,
    submit,
  };
};
